/**
 * Visual overrides for Mantine React Table to match QHS v3 design
 * tokens. These are the props the shared Table component passes to
 * MantineReactTable to apply the design-system look without touching
 * any business logic.
 */

export const buildV2MantineProps = ({ removeVerticalCellPadding = false } = {}) => ({
  // ---- Head cells ----
  // v3 template: calm off-white stripe, text-1 label, normal case,
  // vertical grid rails between columns. The `mantine-TableHeadCell-
  // Content-Labels` / `…-Wrapper` rules reproduce the ellipsis
  // behaviour from the legacy cell so long headers don't wrap.
  tableHeadCellProps: {
    sx: {
      // Header background drops the previous '#fafbfc' tinted stripe
      // and uses the surface (white) instead. Has to be OPAQUE, not
      // transparent — pinned columns are `position: sticky` and a
      // see-through head cell would let the unpinned column body
      // scroll visibly through the header during horizontal scroll.
      // The filter inputs each carry their own border so the row
      // still self-segments visually without a tinted backdrop.
      backgroundColor: 'var(--qhs-surface)',
      borderBottom: '1px solid var(--qhs-border)',
      // Vertical column rail mirrors the body's rail token —
      // OPAQUE soft separator that swaps with the dark-theme
      // equivalent automatically, so pinned column header borders
      // don't bleed through scrolling unpinned content.
      borderRight: '1px solid var(--qhs-table-rail)',
      '&:last-of-type': { borderRight: 'none' },
      color: 'var(--qhs-text-1)',
      fontSize: 13,
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: 0,
      padding: '10px 12px',
      '& .mantine-TableHeadCell-Content-Labels': {
        gap: 6,
        // 9px nudge so the column title text lines up with the filter
        // placeholder text below it: cell horizontal padding is 12px,
        // and the filter input adds another 1px border + 8px inner
        // padding before its placeholder begins. Without this shift
        // the title sits visibly to the left of its own filter.
        paddingLeft: 9,
        '& .mantine-TableHeadCell-Content-Wrapper': {
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          display: 'block',
          lineHeight: 1.3,
        },
      },
      // MRT icon buttons (sort chevron, drag grip, column menu) —
      // mute them so they read as quiet hints, not equal-weight
      // glyphs next to the label. On cell hover, brighten them from
      // text-4 to text-3 (subtle "these are interactive" affordance);
      // on direct icon hover, the inner rule below paints them brand.
      '& .mantine-ActionIcon-root': {
        color: 'var(--qhs-text-4)',
        transition: 'color 150ms ease',
        '&:hover': {
          color: 'var(--qhs-brand)',
          backgroundColor: 'transparent',
        },
      },
      '&:hover .mantine-ActionIcon-root': { color: 'var(--qhs-text-3)' },
      ...(removeVerticalCellPadding && {
        '& .mantine-TableHeadCell-Content': {
          paddingTop: '4px !important',
          paddingBottom: '4px !important',
          border: 'none !important',
        },
      }),
    },
  },

  // ---- Head rows ----
  // Background follows surface (transparent against the table card)
  // so the header reads as one calm block instead of two stripes.
  // The previous `var(--qhs-bg)` here forced a subtle gray that
  // fought the per-cell `transparent` we set above.
  tableHeadRowProps: {
    sx: {
      boxShadow: 'none',
      '& th': { backgroundColor: 'var(--qhs-surface) !important' },
    },
  },

  // ---- Body cells ----
  // Row + column separators are NOT set here because the legacy
  // `tableBodyProp` (lib/table_state_styles.js) sets
  // `border: none !important` on `& td` with specificity 0,1,1 that
  // beats this cell-level `sx` rule (specificity 0,1,0) regardless of
  // `!important`. The actual border rules live in `Table.css` under
  // a `.qhs-table-v3 .mantine-Table-root tbody td` compound selector
  // (specificity 0,2,2) so they win.
  // NOTE: this block is built but no longer wired into the table —
  // perf-fix #4 in Table.jsx switched `mantineTableBodyCellProps` to
  // a per-cell callback returning frozen `_MONO_PROPS` / `_EMPTY_PROPS`
  // (no sx). The body's font-family / font-size / colour are already
  // set in Table.css under `.qhs-table-v3 .mantine-Table-root tbody td`
  // and horizontal alignment with the header / filter row lives there
  // too. Kept here as documentation of the v2 visual contract, in case
  // a non-perf consumer pulls it directly.
  tableBodyCellProps: {
    sx: {
      fontFamily: 'var(--qhs-font-body)',
      fontSize: 13,
      color: 'var(--qhs-text-1)',
      padding: '12px 12px',
    },
  },
});

export const buildV2TableBodyProps = ({
  onRowClick,
  renderRowActionMenuItems,
  renderRowActions,
  removeVerticalCellPadding = false,
} = {}) => ({
  sx: {
    '& tr': {
      transition: 'background-color 120ms ease',
      ...(onRowClick ? { cursor: 'pointer' } : {}),
      '&:hover td': {
        backgroundColor: 'var(--qhs-surface-hover) !important',
      },
    },
    '& td:last-child': {
      '& .mantine-Skeleton-root': {
        marginLeft: !!renderRowActionMenuItems || !!renderRowActions ? 12 : 0,
      },
    },
    '& td:has(.more-button)': { padding: 0 },
    ...(removeVerticalCellPadding && {
      '& td': {
        paddingTop: '2px !important',
        paddingBottom: '2px !important',
        border: 'none !important',
      },
    }),
    '& tr:has(.table-fallback):hover td': {
      backgroundColor: 'transparent !important',
      cursor: 'default',
    },
    '& tr:has(.table-fallback) td': {
      backgroundColor: 'transparent !important',
      border: 'none !important',
    },
    /* New-row highlight (alerts table) — brand-tinted, not harsh yellow */
    '& tr[data-viewed="false"] td': {
      backgroundColor: 'rgba(46, 72, 211, 0.06) !important',
      animation: 'qhs-row-fade 1500ms ease-out',
    },
    '@keyframes qhs-row-fade': {
      from: { backgroundColor: 'rgba(46, 72, 211, 0.15)' },
      to: { backgroundColor: 'rgba(46, 72, 211, 0.06)' },
    },
  },
});
